import { useCallback, useEffect, useMemo, useRef } from 'react'
import type { HotelMapViewData, ExperienceMapViewData, TripStop } from 'tripPlanner/api/types'
import type { DrawerHotel } from '../components/HotelListingDrawer'
import TripPlanCanvas from '../components/TripPlanCanvas'
import DayItineraryCanvas from '../components/DayItineraryCanvas'
import { DayItineraryOverlay } from '../styles/layoutStyles'
import { useTripPlannerEventBus, useEventSubscription } from '../contexts/TripPlannerEventBus'
import { useChatActions } from '../contexts/ChatActionsContext'
import { useChatStreamStatus } from '../contexts/ChatStreamStatusContext'
import { useItineraryContext } from '../contexts/ItineraryContext'

interface Props {
  /** When false, hooks still run (preserving fingerprint tracking) but JSX returns null */
  visible: boolean
  selectedDayItineraryId: number | undefined
  selectedStop: TripStop | undefined
}

function ItineraryContainer({ visible, selectedDayItineraryId, selectedStop }: Props) {
  const eventBus = useTripPlannerEventBus()
  const { sendMessage } = useChatActions()
  const { isStreaming } = useChatStreamStatus()
  const {
    itineraryData,
    hasItineraryData,
    enrichingStopIds,
    scrollTarget,
    clearScrollTarget,
    refetchCurrentChatItinerary,
  } = useItineraryContext()

  // ── Fingerprint tracking ──

  const prevItineraryFingerprintRef = useRef<string | null>(null)
  const prevHasItineraryRef = useRef(false)

  const itineraryFingerprint = useMemo(() => {
    if (!itineraryData?.stops) return null

    return JSON.stringify({
      stopCount: itineraryData.stops.length,
      stopIds: itineraryData.stops.map(s => s.id),
      itemCounts: itineraryData.stops.map(s =>
        s.days.reduce((acc: number, d: { items: Array<unknown> }) => acc + d.items.length, 0) +
        (s.itemsWithoutDay?.length ?? 0),
      ),
    })
  }, [itineraryData])

  useEffect(() => {
    const currentFingerprint = itineraryFingerprint
    const previousFingerprint = prevItineraryFingerprintRef.current
    const itineraryChanged = previousFingerprint !== null && currentFingerprint !== previousFingerprint
    const itineraryAppeared = hasItineraryData && !prevHasItineraryRef.current

    if (itineraryAppeared || itineraryChanged) {
      eventBus.dispatch({ type: 'itinerary_changed' })
    }

    prevHasItineraryRef.current = hasItineraryData
    prevItineraryFingerprintRef.current = currentFingerprint
  }, [hasItineraryData, itineraryFingerprint, eventBus])

  // ── Event bus subscriptions ──

  useEventSubscription('reset_chat_state', () => {
    prevItineraryFingerprintRef.current = null
    prevHasItineraryRef.current = false
  })

  // ── Callback wrappers dispatching cross-container events ──

  const handleLearnMoreClick = useCallback((googlePlaceId: string, placeName: string, countryName: string, stopId?: number) => {
    eventBus.dispatch({ type: 'open_detail_drawer', googlePlaceId, placeName, countryName, stopId })
  }, [eventBus])

  const handleExploreMapClick = useCallback((coordinates: { lat: number; lng: number }) => {
    eventBus.dispatch({ type: 'show_explore_on_map', coordinates })
  }, [eventBus])

  const handleShowMap = useCallback((data: HotelMapViewData) => {
    eventBus.dispatch({ type: 'show_hotel_on_map', data })
  }, [eventBus])

  const handleShowExperienceOnMap = useCallback((data: ExperienceMapViewData) => {
    eventBus.dispatch({ type: 'show_experience_on_map', data })
  }, [eventBus])

  const handleAskAIForDay = useCallback((dayNumber: number, dayTitle: string, date?: string) => {
    eventBus.dispatch({ type: 'ask_ai_for_day', dayNumber, title: dayTitle, date })
  }, [eventBus])

  const handleOfferShowMap = useCallback((hotel: DrawerHotel) => {
    if (!hotel.geoLocation) return
    eventBus.dispatch({
      type: 'show_hotel_on_map',
      data: {
        searchQuery: hotel.location ?? hotel.name,
        offers: [{ id: hotel.id, name: hotel.name, coordinates: hotel.geoLocation }],
      },
    })
  }, [eventBus])

  // ── Render ──

  // Convention exception: "Components should not determine whether they render"
  // This container must stay mounted so the fingerprint tracking effect detects
  // itinerary changes regardless of which view is active. The parent controls
  // visibility via the `visible` prop; the child only gates JSX output.
  if (!visible) return null

  const stopId = selectedStop?.id
  const shouldShowDayItinerary = selectedDayItineraryId && selectedStop && selectedStop.days.length > 0

  return (
    <>
      <TripPlanCanvas
        data={itineraryData}
        onLearnMoreClick={handleLearnMoreClick}
        onRefetchItinerary={refetchCurrentChatItinerary}
        onDayDeleted={refetchCurrentChatItinerary}
        onSendMessage={sendMessage}
        isStreaming={isStreaming}
        onExploreMap={handleExploreMapClick}
        onShowMap={handleShowMap}
        onShowExperienceOnMap={handleShowExperienceOnMap}
        enrichingStopIds={enrichingStopIds}
        onAskAI={handleAskAIForDay}
        scrollTarget={scrollTarget}
        onScrollComplete={clearScrollTarget}
      />
      {shouldShowDayItinerary && (
        <DayItineraryOverlay>
          <DayItineraryCanvas
            data={itineraryData}
            stopId={stopId}
            onDayDeleted={refetchCurrentChatItinerary}
            onShowMap={handleOfferShowMap}
            onShowExperienceOnMap={handleShowExperienceOnMap}
          />
        </DayItineraryOverlay>
      )}
    </>
  )
}

export default ItineraryContainer
